import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Input, SpecraCombobox } from '@mono/ui-components';
import { ArrowRightLeft, ChevronLeft, LogOut, Search, Users, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody } from '../../components/layout';
import { PageHeader } from '../../components/PageHeader';
import { LeaveProjectDialog } from '../../components/ProjectDashboard/LeaveProjectDialog';
import { MemberList } from '../../components/ProjectDashboard/MemberList';
import { useAuth } from '../../contexts/authContext';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useAddMembers, useArchiveProject, useTransferOwnership } from '../../hooks/useProjects';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
import { sharingApi } from '../../lib/api/sharingApi';
/**
 * Project team/members management page.
 * Extracted from ProjectDashboard with full functionality.
 */ export function ProjectTeam() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { projectId, project, members, isLoading, error, isOwner, isArchived } = useProjectContext();
    const [leaveProjectDialogOpen, setLeaveProjectDialogOpen] = useState(false);
    // Member search state
    const [memberSearchQuery, setMemberSearchQuery] = useState('');
    const [memberSearchResults, setMemberSearchResults] = useState([]);
    const [selectedNewMembers, setSelectedNewMembers] = useState([]);
    const [isSearchingMembers, setIsSearchingMembers] = useState(false);
    const searchTimeoutRef = useRef(null);
    // Transfer ownership state
    const [showTransferSection, setShowTransferSection] = useState(false);
    const [transferTargetId, setTransferTargetId] = useState('');
    const [showTransferConfirm, setShowTransferConfirm] = useState(false);
    const addMembers = useAddMembers();
    const transferOwnership = useTransferOwnership();
    const archiveProject = useArchiveProject();
    const currentUserId = user?.id ?? '';
    const memberCount = members.length;
    // Debounced member search
    useEffect(()=>{
        if (!memberSearchQuery.trim()) {
            setMemberSearchResults([]);
            setIsSearchingMembers(false);
            return;
        }
        setIsSearchingMembers(true);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(async ()=>{
            try {
                const results = await sharingApi.searchUsers(memberSearchQuery.trim());
                const existingMemberIds = new Set(members.map((m)=>m.userId));
                const selectedIds = new Set(selectedNewMembers.map((u)=>u.userId));
                const filtered = results.filter((u)=>!existingMemberIds.has(u.userId) && !selectedIds.has(u.userId));
                setMemberSearchResults(filtered);
            } catch  {
                setMemberSearchResults([]);
            } finally{
                setIsSearchingMembers(false);
            }
        }, 300);
        return ()=>{
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [
        memberSearchQuery,
        members,
        selectedNewMembers
    ]);
    const handleSelectUser = useCallback((user, role)=>{
        setSelectedNewMembers((prev)=>[
                ...prev,
                {
                    ...user,
                    role
                }
            ]);
        setMemberSearchQuery('');
        setMemberSearchResults([]);
    }, []);
    const handleRemoveSelectedUser = useCallback((userId)=>{
        setSelectedNewMembers((prev)=>prev.filter((u)=>u.userId !== userId));
    }, []);
    const handleAddSelectedMembers = async ()=>{
        if (!projectId || selectedNewMembers.length === 0) return;
        try {
            await addMembers.mutateAsync({
                projectId,
                members: selectedNewMembers.map((u)=>({
                        userId: u.userId,
                        email: u.email,
                        name: u.name,
                        role: u.role
                    }))
            });
            toast.success(`Added ${selectedNewMembers.length} member${selectedNewMembers.length > 1 ? 's' : ''}`);
            setSelectedNewMembers([]);
            setMemberSearchQuery('');
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to add members');
            toast.error(message);
        }
    };
    const resetTransferOwnership = useCallback(()=>{
        setShowTransferSection(false);
        setShowTransferConfirm(false);
        setTransferTargetId('');
    }, []);
    const handleTransferOwnership = async ()=>{
        if (!projectId || !transferTargetId) return;
        const targetMember = members.find((m)=>m.userId === transferTargetId);
        try {
            await transferOwnership.mutateAsync({
                projectId,
                newOwnerId: transferTargetId
            });
            toast.success(`Ownership transferred to ${targetMember?.name}`);
            resetTransferOwnership();
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to transfer ownership');
            toast.error(message);
        }
    };
    const handleArchive = async ()=>{
        if (!projectId) return;
        try {
            await archiveProject.mutateAsync(projectId);
            toast.success('Project archived successfully');
            navigate('/projects');
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to archive project');
            toast.error(message);
        }
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-4 w-24 bg-muted rounded animate-pulse mb-2"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-32 bg-muted rounded animate-pulse"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "space-y-4 max-w-2xl",
                        "aria-busy": "true",
                        children: [
                            1,
                            2,
                            3,
                            4
                        ].map((i)=>/*#__PURE__*/ _jsx("div", {
                                className: "h-16 bg-muted rounded-lg animate-pulse"
                            }, i))
                    })
                })
            ]
        });
    }
    if (error || !project || !projectId) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: "/projects",
                                className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Projects"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Project not found"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center py-12",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-red-600 mb-4",
                                children: "This project doesn't exist or you don't have access."
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                onClick: ()=>navigate('/projects'),
                                children: "Back to Projects"
                            })
                        ]
                    })
                })
            ]
        });
    }
    const projectDetail = {
        ...project,
        members,
        role: project.role,
        memberCount
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-xl font-semibold",
                            children: "Team"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Project members"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "max-w-2xl",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 mb-6",
                                children: [
                                    /*#__PURE__*/ _jsx(Users, {
                                        className: "w-5 h-5 text-muted-foreground"
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-lg font-medium",
                                        children: [
                                            "Members (",
                                            memberCount,
                                            "/25)"
                                        ]
                                    })
                                ]
                            }),
                            isOwner && !isArchived && memberCount < 25 && /*#__PURE__*/ _jsxs("div", {
                                className: "mb-6 p-4 border rounded-lg",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "font-medium mb-3",
                                        children: "Add Members"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "relative",
                                        children: [
                                            /*#__PURE__*/ _jsx(Search, {
                                                className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                                            }),
                                            /*#__PURE__*/ _jsx(Input, {
                                                type: "text",
                                                placeholder: "Search users to add...",
                                                value: memberSearchQuery,
                                                onChange: (e)=>setMemberSearchQuery(e.target.value),
                                                className: "pl-9",
                                                "aria-label": "Search users to add"
                                            })
                                        ]
                                    }),
                                    memberSearchQuery.trim() && /*#__PURE__*/ _jsx("div", {
                                        className: "mt-2 border rounded-md bg-background max-h-48 overflow-auto",
                                        children: isSearchingMembers ? /*#__PURE__*/ _jsx("div", {
                                            className: "p-3 text-sm text-muted-foreground text-center",
                                            children: "Searching..."
                                        }) : memberSearchResults.length === 0 ? /*#__PURE__*/ _jsx("div", {
                                            className: "p-3 text-sm text-muted-foreground text-center",
                                            children: "No users found"
                                        }) : memberSearchResults.map((user)=>/*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between p-2 hover:bg-muted border-b last:border-b-0",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex-1 min-w-0",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "text-sm font-medium truncate",
                                                                children: user.name
                                                            }),
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "text-xs text-muted-foreground truncate",
                                                                children: user.email
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex gap-1 ml-2",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Button, {
                                                                variant: "ghost",
                                                                size: "sm",
                                                                onClick: ()=>handleSelectUser(user, 'member'),
                                                                className: "text-xs h-7 px-2",
                                                                children: "Member"
                                                            }),
                                                            /*#__PURE__*/ _jsx(Button, {
                                                                variant: "ghost",
                                                                size: "sm",
                                                                onClick: ()=>handleSelectUser(user, 'view_only'),
                                                                className: "text-xs h-7 px-2",
                                                                children: "View Only"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }, user.userId))
                                    }),
                                    selectedNewMembers.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-3 flex flex-wrap items-center gap-2",
                                        children: [
                                            selectedNewMembers.map((u)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: "inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-full text-sm",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "truncate max-w-[120px]",
                                                            children: u.name
                                                        }),
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            className: "text-xs text-muted-foreground",
                                                            children: [
                                                                "(",
                                                                u.role.replace('_', ' '),
                                                                ")"
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("button", {
                                                            type: "button",
                                                            onClick: ()=>handleRemoveSelectedUser(u.userId),
                                                            className: "ml-1 text-muted-foreground hover:text-foreground",
                                                            "aria-label": `Remove ${u.name}`,
                                                            children: /*#__PURE__*/ _jsx(X, {
                                                                className: "w-3 h-3"
                                                            })
                                                        })
                                                    ]
                                                }, u.userId)),
                                            /*#__PURE__*/ _jsx(Button, {
                                                size: "sm",
                                                onClick: handleAddSelectedMembers,
                                                disabled: addMembers.isPending,
                                                className: "h-7",
                                                children: addMembers.isPending ? 'Adding...' : `Add ${selectedNewMembers.length}`
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(MemberList, {
                                project: projectDetail,
                                currentUserId: currentUserId
                            }),
                            !isArchived && /*#__PURE__*/ _jsxs("div", {
                                className: "mt-6 pt-4 border-t",
                                children: [
                                    isOwner && /*#__PURE__*/ _jsx("div", {
                                        className: "mb-4",
                                        children: !showTransferSection ? /*#__PURE__*/ _jsxs(Button, {
                                            variant: "ghost",
                                            size: "sm",
                                            onClick: ()=>setShowTransferSection(true),
                                            className: "text-muted-foreground",
                                            children: [
                                                /*#__PURE__*/ _jsx(ArrowRightLeft, {
                                                    className: "w-4 h-4 mr-1"
                                                }),
                                                "Transfer Ownership"
                                            ]
                                        }) : /*#__PURE__*/ _jsxs("div", {
                                            className: "p-3 border rounded-md bg-muted/50",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center justify-between mb-3",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-sm font-medium",
                                                            children: "Transfer Ownership"
                                                        }),
                                                        /*#__PURE__*/ _jsx("button", {
                                                            type: "button",
                                                            onClick: resetTransferOwnership,
                                                            className: "text-muted-foreground hover:text-foreground",
                                                            "aria-label": "Cancel transfer",
                                                            children: /*#__PURE__*/ _jsx(X, {
                                                                className: "w-4 h-4"
                                                            })
                                                        })
                                                    ]
                                                }),
                                                !showTransferConfirm ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "mb-3",
                                                            children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                                                                label: "New owner",
                                                                options: members.filter((m)=>m.userId !== currentUserId && m.role !== 'owner').map((member)=>({
                                                                        value: member.userId,
                                                                        label: `${member.name} (${member.email})`
                                                                    })),
                                                                value: transferTargetId || undefined,
                                                                onValueChange: (v)=>setTransferTargetId(v ?? ''),
                                                                placeholder: "Select new owner...",
                                                                searchPlaceholder: "Search members by name or email…"
                                                            })
                                                        }),
                                                        /*#__PURE__*/ _jsx(Button, {
                                                            size: "sm",
                                                            variant: "outline",
                                                            onClick: ()=>setShowTransferConfirm(true),
                                                            disabled: !transferTargetId,
                                                            className: "w-full",
                                                            children: "Continue"
                                                        })
                                                    ]
                                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "p-3 bg-yellow-50 border border-yellow-200 rounded-md mb-3",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("p", {
                                                                    className: "text-sm font-medium text-yellow-900 mb-2",
                                                                    children: "Warning: This cannot be undone"
                                                                }),
                                                                /*#__PURE__*/ _jsxs("ul", {
                                                                    className: "text-xs text-yellow-800 space-y-1 list-disc list-inside",
                                                                    children: [
                                                                        /*#__PURE__*/ _jsxs("li", {
                                                                            children: [
                                                                                /*#__PURE__*/ _jsx("strong", {
                                                                                    children: members.find((m)=>m.userId === transferTargetId)?.name
                                                                                }),
                                                                                ' ',
                                                                                "will become the new owner"
                                                                            ]
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("li", {
                                                                            children: "You will be demoted to Member role"
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("li", {
                                                                            children: "The new owner will have full control"
                                                                        })
                                                                    ]
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex gap-2",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Button, {
                                                                    size: "sm",
                                                                    variant: "outline",
                                                                    onClick: ()=>setShowTransferConfirm(false),
                                                                    disabled: transferOwnership.isPending,
                                                                    className: "flex-1",
                                                                    children: "Back"
                                                                }),
                                                                /*#__PURE__*/ _jsx(Button, {
                                                                    size: "sm",
                                                                    variant: "destructive",
                                                                    onClick: handleTransferOwnership,
                                                                    disabled: transferOwnership.isPending,
                                                                    className: "flex-1",
                                                                    children: transferOwnership.isPending ? 'Transferring...' : 'Transfer'
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    }),
                                    !isOwner && /*#__PURE__*/ _jsxs(Button, {
                                        variant: "ghost",
                                        size: "sm",
                                        onClick: ()=>setLeaveProjectDialogOpen(true),
                                        className: "text-red-500 hover:text-red-600 hover:bg-red-50",
                                        children: [
                                            /*#__PURE__*/ _jsx(LogOut, {
                                                className: "w-4 h-4 mr-1"
                                            }),
                                            "Leave Project"
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(LeaveProjectDialog, {
                        open: leaveProjectDialogOpen,
                        onOpenChange: setLeaveProjectDialogOpen,
                        project: projectDetail,
                        currentUserId: currentUserId,
                        onTransferOwnership: ()=>{
                            setLeaveProjectDialogOpen(false);
                            setShowTransferSection(true);
                        },
                        onArchiveProject: handleArchive
                    })
                ]
            })
        ]
    });
}
